import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { t } from "@lingui/macro";
import {
  ItemTop,
  ItemDetails,
  i18n,
  PivotMenu,
  StringToHTML,
  MessageBox,
  PagedDetailList
} from "idc-ui-react";
// Fabric Styling
import { AnimationStyles } from "@uifabric/styling";
// Fluent UI
import {
  Stack,
  Text,
  PivotItem,
  Modal,
  mergeStyleSets,
  IconButton,
  Link,
  getTheme
} from "@fluentui/react";
import { useBoolean } from "@uifabric/react-hooks";
// Our modules
import { getFileIconProps } from "../../../utilities/NavMenuHelper";
import { ReportHTMLView } from "../ReportTypes/ReportHTMLView";
import { ReportDownloadView } from "../ReportTypes/ReportDownloadView";
import { PopExpandButton, DownloadCSVButton } from "../../Buttons";
import { store } from "../../../stores/Main";
import { reportColumns } from "../listConf";

const ReportItem = () => {
  const {
    state,
    notifications,
    orderAuditlogCSV,
    getPagingReport,
    getEmailReport
  } = store;
  const { item, report, isFetchingReport, isFetchingReportTotal } = state;

  const [buttons, setButtons] = useState();
  const [selectedTab, setSelectedTabs] = useState();
  const [tabs, setTabs] = useState();
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(
    false
  );
  const location = useLocation();

  // Item handler
  useEffect(() => {
    if (!item || !item.tabs) return;
    setTabs(item.tabs);
    setSelectedTabs(item.tabs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Button handler
  useEffect(() => {
    if (!item) return;
    const itemList = [];
    if (item.responseType === "LINK") {
      itemList.push(
        DownloadCSVButton(
          () => getEmailReport(item.endpoint),
          i18n._(t`Generate report`)
        )
      );
    } else if (item.reportType === "AUDITLOG") {
      itemList.push(
        DownloadCSVButton(
          handleDownloadAuditlog,
          i18n._(t`Generate CSV report`)
        )
      );
    }
    if (item.fileType === "HTML") itemList.push(PopExpandButton(showModal));

    setButtons(itemList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, report, isFetchingReport, item]);

  // Modal shows HTML report in full screen size
  useEffect(() => {
    if (isModalOpen) getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  async function handleDownloadAuditlog() {
    const queryParams = new URLSearchParams(location.search);
    let params = [];

    queryParams.forEach((value, key) => {
      params.push(`${key}=${value}`);
    });

    const success = orderAuditlogCSV(params, report);

    if (!success) {
      notifications.setError(() =>
        i18n._(
          t`There was an error generating the report. Please contact your system administrator if the problem persist`
        )
      );
      return;
    }

    notifications.setSuccess(() =>
      i18n._(
        t`The report is generating and will be sent to your email once its done`
      )
    );
  }

  // Called when HTML report is shown in full screen size
  async function getReport() {
    store.getReportItem(item.id);
  }

  if (!item) return <></>;

  return (
    <Stack
      className={AnimationStyles.fadeIn200}
      styles={{
        root: { backgroundColor: "#fff" }
      }}
    >
      <Modal
        isOpen={isModalOpen}
        isBlocking={true}
        containerClassName={contentStyles.container}
        onDismiss={hideModal}
        styles={{
          root: {
            paddingLeft: "15px",
            display: "flex",
            flexFlow: "column nowrap",
            alignItems: "stretch"
          }
        }}
      >
        <IconButton
          styles={{
            root: {
              color: theme.palette.neutralPrimary,
              marginLeft: "auto",
              marginTop: "4px",
              marginRight: "2px",
              position: "relative",
              float: "right"
            },
            rootHovered: {
              color: theme.palette.neutralDark
            }
          }}
          iconProps={{ iconName: "Cancel" }}
          ariaLabel="Close popup modal"
          onClick={hideModal}
        />
        <ItemTop
          text={item.name}
          avatarProps={{
            iconName: getFileIconProps(item.fileType).iconName,
            iconColor: getFileIconProps(item.fileType).color
          }}
        />
        <ReportHTMLView fullPage={true} />
      </Modal>
      <ItemTop
        buttons={buttons}
        text={item.name}
        avatarProps={{
          iconName: getFileIconProps(item.fileType).iconName,
          iconColor: getFileIconProps(item.fileType).color
        }}
      />
      <ItemDetails>
        <Stack
          styles={{
            root: {
              paddingBottom: 4,
              borderBottom: "1px solid rgb(243, 242, 241)"
            }
          }}
        >
          <Stack
            styles={{
              root: {
                width: "80%",
                paddingRight: "20px"
              }
            }}
          >
            <Text variant="mediumPlus" style={{ fontStyle: "italic" }}>
              <StringToHTML text={item.description}></StringToHTML>
            </Text>
          </Stack>
        </Stack>
        {item.fileType === "NONE" && report && report.totalElements >= 1000000 && (
          <MessageBox
            text={i18n._(t`Note:`)}
            messageBoxType="warning"
            variant="small"
          >
            {i18n._(
              t`CSV files of more than 1.000.000 rows cannot be imported into Excel.`
            )}
            <Link
              href="https://usermanual.idconnect.dk/vejledninger/rapporter/rapport-auditlog/hent-auditlog-rapporten-som-csv-fil"
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n._(t`Read more in the user manual`)}
            </Link>
          </MessageBox>
        )}
        <Stack>
          {item.responseType === "LINK" ? (
            <></>
          ) : item.fileType === "NONE" ? (
            <PivotMenu
              selectedKey={selectedTab}
              onLinkClick={
                //we don't need selectedTabs for now unless we get more reports
                /*item => setSelectedTabs(item.props.itemKey)*/ () => {}
              }
            >
              {tabs &&
                tabs.map(tab => (
                  <PivotItem
                    key={tab.id}
                    itemKey={tab.id}
                    headerText={tab.name}
                  >
                    <Stack tokens={{ childrenGap: 8 }}>
                      <PagedDetailList
                        item={tab}
                        pagingCountAvailable={item.pagingCountAvailable}
                        isFetching={isFetchingReport}
                        isFetchingTotal={isFetchingReportTotal}
                        contents={report}
                        listColumn={reportColumns}
                        getPagingReport={getPagingReport}
                      />
                    </Stack>
                  </PivotItem>
                ))}
            </PivotMenu>
          ) : item.fileType === "HTML" ? (
            <ReportHTMLView />
          ) : (
            <ReportDownloadView item={item} />
          )}
        </Stack>
      </ItemDetails>
    </Stack>
  );
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
    height: "calc(100vh - (100vh - 100%))"
  }
});

export default ReportItem;
